import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { db, auth, OperationType, handleFirestoreError } from '../firebase';
import { 
  collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc, 
  onSnapshot, query, where, orderBy, limit 
} from 'firebase/firestore';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { toast } from 'react-hot-toast';
import { medicalInstrumentsSeed } from '../data/seedData';
import { LOGO_ICON } from '../assets/logos';

import { SiteSettings, Instrument, CartItem, InstrumentContextType } from '../types';
import { safeStorage, safeSessionStorage } from '../utils/storage';
import { optimizeImageUrl } from '../utils/imageOptimizer';
import { sortInstrumentsWithUserPriority, isUserCustomProduct } from '../utils/productPriority';

export type Language = 'en' | 'ur' | 'ar';
export type Currency = 'USD' | 'PKR' | 'CNY' | 'SAR' | 'EUR';

export const DEFAULT_SETTINGS: SiteSettings = {
  heroTitle: "Precision Surgical Instrumentation",
  heroSubtitle: "Global Excellence in Medical Manufacturing",
  categories: ['Urology', 'Laparoscopic', 'Gynecology', 'Orthopedic', 'General Surgery', 'Electrosurgical', 'Dr to Dr Hub'],
  autoBloggingEnabled: true,
  autoBloggingIntervalHours: 24,
  socialLinks: {},
  apkUrl: '/api/download-apk',
  apkFileName: 'hamim-enterprises-app.apk',
  apkFileSize: 'Ready for Download',
  apkUploadedAt: '',
  playStoreUrl: '#',
  appStoreUrl: '#',
  appBannerEnabled: true,
  appBannerTitle: 'Hamim Enterprises Official Mobile App',
  appVersionLabel: 'v1.0.0 (Official Edition)',
  appBannerSubtitle: 'Fast 1-tap surgical catalog, product comparison, offline viewing & direct WhatsApp quotation',
  appReleaseNotes: 'Complete Surgical Instruments catalog, instant product inquiry, automated RFQ generator, offline browsing support'
};

// --- Contexts ---
export const SiteSettingsContext = createContext<{
  settings: SiteSettings;
  updateSettings: (newSettings: Partial<SiteSettings>) => Promise<void>;
  loading: boolean;
}>({ settings: DEFAULT_SETTINGS, updateSettings: async () => {}, loading: true });

export const LanguageContext = createContext<{
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}>({ language: 'en', setLanguage: () => {}, t: (k) => k });

export const CurrencyContext = createContext<{
  currency: Currency;
  setCurrency: (curr: Currency) => void;
  formatPrice: (price: number) => string;
  formatAllPrices: (price: number) => { usd: string; local: string; pkr: string; sar: string; eur: string; cny: string; };
  rates: Record<Currency, number>;
}>({ 
  currency: 'USD', 
  setCurrency: () => {}, 
  formatPrice: (p) => `${p}`,
  formatAllPrices: (p) => ({ usd: `${p}`, local: `${p}`, pkr: `Rs${p}`, sar: `SR${p}`, eur: `€${p}`, cny: `¥${Math.round(p * 7.2)}` }),
  rates: { USD: 1, PKR: 280, CNY: 7.2, SAR: 3.75, EUR: 0.92 }
});

export const InstrumentContext = createContext<InstrumentContextType>({ 
  instruments: [], 
  loading: true, 
  refresh: async () => {},
  addInstrument: async () => '',
  updateInstrument: async () => {},
  deleteInstrument: async () => {},
  duplicateInstrument: async () => '',
  updateStock: async () => {},
  restoreSampleCatalog: async () => {}
});

export const CartContext = createContext<{
  cart: any[];
  addToCart: (item: any, quantity?: number) => void;
  removeFromCart: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  total: number;
  isCartOpen: boolean;
  setIsCartOpen: (open: boolean) => void;
  isCheckoutOpen: boolean;
  setIsCheckoutOpen: (open: boolean) => void;
}>({
  cart: [],
  addToCart: () => {},
  removeFromCart: () => {},
  updateQuantity: () => {},
  clearCart: () => {},
  total: 0,
  isCartOpen: false,
  setIsCartOpen: () => {},
  isCheckoutOpen: false,
  setIsCheckoutOpen: () => {}
});

export const WishlistContext = createContext<{
  wishlist: string[];
  toggleWishlist: (itemId: string) => void;
  isInWishlist: (itemId: string) => boolean;
}>({ wishlist: [], toggleWishlist: () => {}, isInWishlist: () => false });

export const CompareContext = createContext<{
  compareItems: any[];
  toggleCompare: (item: any) => void;
  isInCompare: (itemId: string) => boolean;
  clearCompare: () => void;
}>({ compareItems: [], toggleCompare: () => {}, isInCompare: () => false, clearCompare: () => {} });

// --- Hooks ---
export const useSiteSettings = () => useContext(SiteSettingsContext);
export const useLanguage = () => useContext(LanguageContext);
export const useCurrency = () => useContext(CurrencyContext);
export const useInstruments = () => useContext(InstrumentContext);
export const useCart = () => useContext(CartContext);
export const useWishlist = () => useContext(WishlistContext);
export const useCompare = () => useContext(CompareContext);

// --- Providers ---

export const SiteSettingsProvider = ({ children }: { children: React.ReactNode }) => {
  const [settings, setSettings] = useState<SiteSettings>(() => {
    try {
      const cached = safeStorage.getItem<Partial<SiteSettings> | null>('hamim_settings_v1', null);
      if (cached) {
        return { ...DEFAULT_SETTINGS, ...cached };
      }
      return DEFAULT_SETTINGS;
    } catch {
      return DEFAULT_SETTINGS;
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'site_settings', 'general'), (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data() as Partial<SiteSettings>;
        
        // Cleanse any old misspelled title or legacy placeholder subtitle
        let cleanedTitle = data.appBannerTitle;
        if (!cleanedTitle || cleanedTitle === 'Hami Enterprises Official Mobile App' || cleanedTitle === 'Hami Enterprises') {
          cleanedTitle = 'Hamim Enterprises Official Mobile App';
        }
        
        let cleanedSubtitle = data.appBannerSubtitle;
        if (cleanedSubtitle && (cleanedSubtitle.toLowerCase().includes('med nova') || cleanedSubtitle.includes('مڈ نوا'))) {
          cleanedSubtitle = DEFAULT_SETTINGS.appBannerSubtitle;
        }

        setSettings(prev => {
          const updated = { 
            ...prev, 
            ...data,
            appBannerTitle: cleanedTitle || prev.appBannerTitle,
            appBannerSubtitle: cleanedSubtitle || prev.appBannerSubtitle
          };
          safeStorage.setItem('hamim_settings_v1', updated, { sanitize: true });
          return updated;
        });
      }
      setLoading(false);
    }, (error) => {
      console.warn("Site settings listener warning:", error);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const updateSettings = async (newSettings: Partial<SiteSettings>) => {
    try {
      await setDoc(doc(db, 'site_settings', 'general'), newSettings, { merge: true });
      // Also update local state immediately for snappy response
      setSettings(prev => {
        const updated = { ...prev, ...newSettings };
        safeStorage.setItem('hamim_settings_v1', updated, { sanitize: true });
        return updated;
      });
      toast.success('Settings updated successfully!');
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'site_settings/general');
    }
  };

  return (
    <SiteSettingsContext.Provider value={{ settings, updateSettings, loading }}>
      {children}
    </SiteSettingsContext.Provider>
  );
};

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  const [language, setLanguage] = useState<Language>(() => {
    return safeStorage.getItem<Language>('he_lang', 'en') || 'en';
  });
  
  useEffect(() => {
    safeStorage.setItem('he_lang', language);
  }, [language]);

  const t = useCallback((key: string) => {
    return key; 
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const CurrencyProvider = ({ children }: { children: React.ReactNode }) => {
  const [currency, setCurrency] = useState<Currency>(() => {
    return safeStorage.getItem<Currency>('he_currency', 'USD') || 'USD';
  });
  const rates: Record<Currency, number> = { USD: 1, PKR: 280, CNY: 7.2, SAR: 3.75, EUR: 0.92 };

  useEffect(() => {
    safeStorage.setItem('he_currency', currency);
  }, [currency]);

  const formatPrice = (price: number) => {
    const converted = price * rates[currency];
    switch (currency) {
      case 'PKR': return `Rs ${converted.toLocaleString()}`;
      case 'CNY': return `¥ ${converted.toLocaleString()}`;
      case 'SAR': return `${converted.toLocaleString()} SR`;
      case 'EUR': return `€${converted.toLocaleString()}`;
      default: return `$${converted.toLocaleString()}`;
    }
  };

  const formatAllPrices = (price: number) => {
    const usd = `$${(price * rates.USD).toLocaleString()}`;
    const pkr = `Rs. ${Math.round(price * rates.PKR).toLocaleString()}`;
    const cny = `¥ ${Math.round(price * rates.CNY).toLocaleString()}`;
    const sar = `${(price * rates.SAR).toLocaleString()} SR`;
    const eur = `€${(price * rates.EUR).toLocaleString()}`;
    
    let local = usd;
    switch (currency) {
      case 'PKR': local = pkr; break;
      case 'CNY': local = cny; break;
      case 'SAR': local = sar; break;
      case 'EUR': local = eur; break;
    }

    return { usd, pkr, cny, sar, eur, local };
  };

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, formatPrice, formatAllPrices, rates }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const InstrumentProvider = ({ children }: { children: React.ReactNode }) => {
  const [instruments, setInstruments] = useState<Instrument[]>(() => {
    try {
      const saved = safeStorage.getItem<Instrument[] | null>('he_instruments_cache_v2', null);
      if (saved && Array.isArray(saved) && saved.length > 0) {
        const list = saved.map(item => ({
          ...item,
          imageUrl: optimizeImageUrl(item.imageUrl)
        }));
        return sortInstrumentsWithUserPriority(list) as Instrument[];
      }
      const initialSeed = medicalInstrumentsSeed.map((item, idx) => ({ 
        id: `seed-${idx}`, 
        ...item,
        imageUrl: optimizeImageUrl(item.imageUrl)
      })) as Instrument[];
      return sortInstrumentsWithUserPriority(initialSeed) as Instrument[];
    } catch {
      const fallback = medicalInstrumentsSeed.map((item, idx) => ({ 
        id: `seed-${idx}`, 
        ...item,
        imageUrl: optimizeImageUrl(item.imageUrl)
      })) as Instrument[];
      return sortInstrumentsWithUserPriority(fallback) as Instrument[];
    }
  });
  const [loading, setLoading] = useState(false);

  // Helper to sort instruments: ALL user-custom / user-edited products rank FIRST at the top
  const sortAndMergeInstruments = (items: Instrument[]): Instrument[] => {
    const optimized = [...items].map(item => ({
      ...item,
      imageUrl: optimizeImageUrl(item.imageUrl)
    }));
    return sortInstrumentsWithUserPriority(optimized) as Instrument[];
  };

  // High-reliability catalog synchronization:
  // Loads instantly from the persistent local backend (/api/catalog), ensures zero Firestore quota exhaustion,
  // merges user edits, and syncs user-customized items to disk so new browsers never see default AI stock images.
  useEffect(() => {
    let isMounted = true;

    const loadCatalog = async () => {
      try {
        const res = await fetch('/api/catalog');
        if (res.ok) {
          const serverData = await res.json();
          if (Array.isArray(serverData) && serverData.length > 0) {
            const sorted = sortAndMergeInstruments(serverData);
            if (isMounted) {
              setInstruments(prev => {
                // If the user previously had local edits in safeStorage, protect & sync them
                const cached = safeStorage.getItem<Instrument[] | null>('he_instruments_cache_v2', null);
                let finalItems = sorted;
                if (cached && Array.isArray(cached) && cached.length > 0) {
                  const userEdits = cached.filter(i => isUserCustomProduct(i));
                  if (userEdits.length > 0) {
                    // Send to persistent server disk in background
                    fetch('/api/catalog/sync', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ instruments: userEdits })
                    }).catch(e => console.warn('Catalog sync warning:', e));

                    // Merge user edited items over default items
                    const map = new Map<string, Instrument>();
                    for (const item of sorted) {
                      map.set(item.id, item);
                      if (item.code) map.set(item.code, item);
                    }
                    for (const u of userEdits) {
                      const existing = map.get(u.id) || (u.code ? map.get(u.code) : null);
                      if (existing) {
                        map.set(existing.id, { ...existing, ...u, isUserCustom: true, userProtected: true, isUserEdited: true });
                      } else {
                        map.set(u.id, { ...u, isUserCustom: true, userProtected: true, isUserEdited: true });
                      }
                    }
                    finalItems = sortAndMergeInstruments(Array.from(new Set(map.values())));
                  }
                }
                safeStorage.setItem('he_instruments_cache_v2', finalItems, { sanitize: false });
                return finalItems;
              });
              setLoading(false);
            }
            return;
          }
        }
      } catch (err) {
        console.warn("Backend catalog fetch warning:", err);
      }
      setLoading(false);
    };

    loadCatalog();
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/catalog');
      if (res.ok) {
        const serverData = await res.json();
        if (Array.isArray(serverData) && serverData.length > 0) {
          const sorted = sortAndMergeInstruments(serverData);
          setInstruments(sorted);
          safeStorage.setItem('he_instruments_cache_v2', sorted, { sanitize: false });
        }
      }
    } catch (error) {
      console.warn("Catalog refresh notice:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const addInstrument = async (instrumentData: Partial<Instrument>): Promise<string> => {
    try {
      const newDocRef = doc(collection(db, 'instruments'));
      const id = instrumentData.id || newDocRef.id;
      const now = new Date().toISOString();
      const payload: Instrument = {
        id,
        name: instrumentData.name || 'Untitled Instrument',
        description: instrumentData.description || '',
        price: Number(instrumentData.price) || 0,
        discountPrice: instrumentData.discountPrice ? Number(instrumentData.discountPrice) : null,
        category: instrumentData.category || 'General Surgery',
        imageUrl: instrumentData.imageUrl || '',
        image2Url: instrumentData.image2Url || '',
        image3Url: instrumentData.image3Url || '',
        stock: Number(instrumentData.stock) || 0,
        code: instrumentData.code || `HE-${Math.floor(1000 + Math.random() * 9000)}`,
        origin: instrumentData.origin || 'Made by Hamim',
        brand: instrumentData.brand || 'Hamim Premium',
        condition: instrumentData.condition || 'New',
        material: instrumentData.material || 'Medical Grade Stainless Steel',
        size: instrumentData.size || '',
        measurement: instrumentData.measurement || '',
        length: instrumentData.length || '',
        width: instrumentData.width || '',
        quality: instrumentData.quality || 'German Grade / ISO 13485',
        usageTime: instrumentData.usageTime || 'Reusable',
        usageLimit: instrumentData.usageLimit ? Number(instrumentData.usageLimit) : undefined,
        pricePoint: instrumentData.pricePoint ? Number(instrumentData.pricePoint) : undefined,
        wholesalePrice: instrumentData.wholesalePrice ? Number(instrumentData.wholesalePrice) : undefined,
        trendingPrice: instrumentData.trendingPrice ? Number(instrumentData.trendingPrice) : undefined,
        sterilizationLimit: instrumentData.sterilizationLimit || 'Autoclavable up to 134°C',
        stars: 5,
        reviewsCount: 1,
        views: 0,
        isUserCustom: true,
        userProtected: true,
        createdAt: now,
        updatedAt: now
      };

      // 1. Save to persistent server catalog
      await fetch('/api/catalog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      // 2. Best-effort Firestore write
      try {
        await setDoc(newDocRef, payload);
      } catch (fsErr) {
        console.warn("Firestore sync notice (saved on server & locally):", fsErr);
      }
      
      // 3. Optimistic local update
      setInstruments(prev => {
        const next = [payload, ...prev.filter(i => i.id !== id)];
        safeStorage.setItem('he_instruments_cache_v2', next, { sanitize: false });
        return next;
      });
      toast.success('Instrument added to catalog!');
      return id;
    } catch (error) {
      console.error("Add instrument error:", error);
      toast.error('Failed to add instrument');
      throw error;
    }
  };

  const updateInstrument = async (id: string, instrumentData: Partial<Instrument>): Promise<void> => {
    try {
      const now = new Date().toISOString();
      const cleanData: any = {
        ...instrumentData,
        isUserCustom: true,
        userProtected: true,
        updatedAt: now
      };
      if (cleanData.price !== undefined) cleanData.price = Number(cleanData.price);
      if (cleanData.stock !== undefined) cleanData.stock = Number(cleanData.stock);

      // 1. Save to persistent server catalog
      await fetch(`/api/catalog/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanData)
      });

      // 2. Best-effort Firestore write
      try {
        if (!id.startsWith('seed-')) {
          await updateDoc(doc(db, 'instruments', id), cleanData);
        } else {
          const newRef = doc(collection(db, 'instruments'));
          await setDoc(newRef, { ...cleanData, id: newRef.id, createdAt: now });
        }
      } catch (fsErr) {
        console.warn("Firestore update notice (saved on server & locally):", fsErr);
      }

      // 3. Optimistic local update
      setInstruments(prev => {
        const next = prev.map(i => (i.id === id || i.code === id) ? { ...i, ...cleanData } as Instrument : i);
        safeStorage.setItem('he_instruments_cache_v2', next, { sanitize: false });
        return next;
      });

      toast.success('Instrument updated successfully!');
    } catch (error) {
      console.error("Update instrument error:", error);
      toast.error('Failed to update instrument');
      throw error;
    }
  };

  const deleteInstrument = async (id: string): Promise<void> => {
    try {
      // 1. Delete on server
      await fetch(`/api/catalog/${id}`, { method: 'DELETE' });

      // 2. Best-effort Firestore delete
      try {
        if (!id.startsWith('seed-')) {
          await deleteDoc(doc(db, 'instruments', id));
        }
      } catch (fsErr) {
        console.warn("Firestore delete notice:", fsErr);
      }

      setInstruments(prev => {
        const next = prev.filter(i => i.id !== id && i.code !== id);
        safeStorage.setItem('he_instruments_cache_v2', next, { sanitize: false });
        return next;
      });
      toast.success('Instrument removed from catalog');
    } catch (error) {
      console.error("Delete instrument error:", error);
      toast.error('Failed to remove instrument');
      throw error;
    }
  };

  const duplicateInstrument = async (instrument: Instrument): Promise<string> => {
    try {
      const newDocRef = doc(collection(db, 'instruments'));
      const id = newDocRef.id;
      const now = new Date().toISOString();
      const clonedPayload: Instrument = {
        ...instrument,
        id,
        name: `${instrument.name} (Copy)`,
        code: `${instrument.code || 'HE'}-CPY-${Math.floor(100 + Math.random() * 900)}`,
        createdAt: now,
        updatedAt: now
      };

      await setDoc(newDocRef, clonedPayload);
      setInstruments(prev => [clonedPayload, ...prev]);
      toast.success(`Duplicated "${instrument.name}"`);
      return id;
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'instruments');
      throw error;
    }
  };

  const updateStock = async (id: string, newStock: number): Promise<void> => {
    const stockVal = Math.max(0, newStock);
    try {
      if (!id.startsWith('seed-')) {
        await updateDoc(doc(db, 'instruments', id), {
          stock: stockVal,
          updatedAt: new Date().toISOString()
        });
      }
      setInstruments(prev => prev.map(i => i.id === id ? { ...i, stock: stockVal } : i));
      toast.success(`Stock updated to ${stockVal}`);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `instruments/${id}`);
    }
  };

  const restoreSampleCatalog = async (): Promise<void> => {
    try {
      setLoading(true);
      const now = new Date().toISOString();
      for (const item of medicalInstrumentsSeed) {
        const newRef = doc(collection(db, 'instruments'));
        await setDoc(newRef, {
          ...item,
          id: newRef.id,
          createdAt: now,
          updatedAt: now
        });
      }
      await refresh();
      toast.success('Sample surgical catalog loaded into database!');
    } catch (error) {
      console.error("Error restoring sample catalog:", error);
      toast.error('Failed to load sample catalog');
    } finally {
      setLoading(false);
    }
  };

  return (
    <InstrumentContext.Provider value={{ 
      instruments, 
      loading, 
      refresh,
      addInstrument,
      updateInstrument,
      deleteInstrument,
      duplicateInstrument,
      updateStock,
      restoreSampleCatalog
    }}>
      {children}
    </InstrumentContext.Provider>
  );
};

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [cart, setCart] = useState<any[]>(() => {
    return safeStorage.getItem<any[]>('he_cart', []) || [];
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);

  useEffect(() => {
    safeStorage.setItem('he_cart', cart);
  }, [cart]);

  const addToCart = (item: any, quantity = 1) => {
    setCart(prev => {
      const cartItemId = item.selectedTier ? `${item.id}-${item.selectedTier}` : item.id;
      const existing = prev.find(i => (i.id === cartItemId || (i.id === item.id && i.selectedTier === item.selectedTier)));
      if (existing) {
        return prev.map(i => i === existing ? { ...i, quantity: i.quantity + quantity } : i);
      }
      return [...prev, { ...item, id: cartItemId, originalId: item.id, quantity }];
    });
    setIsCartOpen(true);
  };

  const removeFromCart = (itemId: string) => setCart(prev => prev.filter(i => i.id !== itemId));
  const updateQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
      return;
    }
    setCart(prev => prev.map(i => i.id === itemId ? { ...i, quantity } : i));
  };
  const clearCart = () => setCart([]);
  const total = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  return (
    <CartContext.Provider value={{ 
      cart, addToCart, removeFromCart, updateQuantity, clearCart, total,
      isCartOpen, setIsCartOpen, isCheckoutOpen, setIsCheckoutOpen
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const WishlistProvider = ({ children }: { children: React.ReactNode }) => {
  const [wishlist, setWishlist] = useState<string[]>(() => {
    return safeStorage.getItem<string[]>('he_wishlist', []) || [];
  });

  useEffect(() => {
    safeStorage.setItem('he_wishlist', wishlist);
  }, [wishlist]);

  const toggleWishlist = (itemId: string) => {
    setWishlist(prev => prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]);
  };

  const isInWishlist = (itemId: string) => wishlist.includes(itemId);

  return (
    <WishlistContext.Provider value={{ wishlist, toggleWishlist, isInWishlist }}>
      {children}
    </WishlistContext.Provider>
  );
};

export const CompareProvider = ({ children }: { children: React.ReactNode }) => {
  const [compareItems, setCompareItems] = useState<any[]>([]);

  const toggleCompare = (item: any) => {
    setCompareItems(prev => {
      if (prev.find(i => i.id === item.id)) {
        return prev.filter(i => i.id !== item.id);
      }
      if (prev.length >= 4) {
        toast.error('Compare limit (4) reached');
        return prev;
      }
      return [...prev, item];
    });
  };

  const isInCompare = (itemId: string) => !!compareItems.find(i => i.id === itemId);
  const clearCompare = () => setCompareItems([]);

  return (
    <CompareContext.Provider value={{ compareItems, toggleCompare, isInCompare, clearCompare }}>
      {children}
    </CompareContext.Provider>
  );
};
